import React from "react";
import Flashbar, {
  FlashbarProps,
} from "@amzn/awsui-components-react/polaris/flashbar";
import { useNotifications } from "./hooks";

const Notifications: React.FC = () => {
  const { dismissNotification, notifications } = useNotifications();
  const { items } = notifications;

  // `useNotifications` returns a fresh `dismissNotification` on every render,
  // so it is read through a ref to keep the arming effect keyed on `items`
  // alone rather than re-running on every render.
  const dismissRef = React.useRef(dismissNotification);
  dismissRef.current = dismissNotification;

  // Pending timer handles by notification id. A ref, not state, because every
  // add and every dismiss re-renders this component and a notification that is
  // already counting down must not be re-armed. A timer removes its own entry
  // when it fires, so the map only ever holds handles that are still pending.
  const timers = React.useRef(new Map<string, ReturnType<typeof setTimeout>>());

  React.useEffect(() => {
    const liveIds = new Set(items.map((notification) => notification.id));
    for (const [id, handle] of timers.current) {
      if (!liveIds.has(id)) {
        clearTimeout(handle);
        timers.current.delete(id);
      }
    }

    for (const { id, timeout } of items) {
      if (!timeout || timers.current.has(id)) continue;
      timers.current.set(
        id,
        setTimeout(() => {
          timers.current.delete(id);
          dismissRef.current(id);
        }, timeout),
      );
    }
  }, [items]);

  // Unmount-only cleanup. The arming effect above must therefore be able to
  // re-arm from an empty map.
  React.useEffect(() => {
    const pending = timers.current;
    return () => {
      for (const handle of pending.values()) {
        clearTimeout(handle);
      }
      pending.clear();
    };
  }, []);

  if (items.length === 0) {
    return <></>;
  }

  const barItems: FlashbarProps.MessageDefinition[] = items.map(
    (notification) => ({
      ...notification,
      // After the spread so a payload-supplied onDismiss cannot override it.
      onDismiss: () => dismissRef.current(notification.id),
    }),
  );

  return <Flashbar items={barItems} data-testid="notifications" />;
};

export default Notifications;
